import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button, 
  Divider, 
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  Chip,
  Collapse,
  IconButton,
  Link as MuiLink
} from '@mui/material';
import { 
  LightbulbOutlined as TipIcon,
  LockOutlined as LockIcon,
  ArrowForward as ArrowIcon,
  CheckCircleOutline as CheckIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Launch as LaunchIcon,
  WorkspacePremium as PremiumIcon,
  Payments as PaymentsIcon,
  Psychology as PsychologyIcon,
  EmojiObjects as IdeaIcon,
  AutoGraph as CareerGrowthIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Grid from '../components/Grid';
import { translations } from '../translations';
import PageHeader from '../components/PageHeader';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { articles } from '../content/articles';

interface TipItem {
  title: string;
  description: string;
  longDescription: string;
  icon: React.ReactNode;
  isPremium: boolean;
}

// Create a styled ExpandMore component for the rotation animation
const ExpandMore = styled((props: {
  expand: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const Tips = () => {
  // State to track which tips are expanded
  const [expandedTips, setExpandedTips] = useState<{ [key: number]: boolean }>({});
  const { language } = useLanguage();

  useEffect(() => {
    const previousTitle = document.title;
    const description =
      'Praktische artikelen en gratis tips over loopbaancoaching, solliciteren en professionele ontwikkeling.';
    document.title = 'Loopbaantips en artikelen | Sterk aan het Werk';
    const descriptionElement = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionElement?.content;
    if (descriptionElement) descriptionElement.content = description;
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const createdCanonical = !canonical;
    const previousCanonical = canonical?.href;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://sterk-aanhetwerk.nl/tips';

    return () => {
      document.title = previousTitle;
      if (descriptionElement && previousDescription) descriptionElement.content = previousDescription;
      if (createdCanonical) canonical?.remove();
      else if (previousCanonical) canonical?.setAttribute('href', previousCanonical);
    };
  }, []);

  // Career tips content in Dutch and English
  const careerTipsContent = {
    nl: [
      {
        title: "Scherp je CV aan met deze 5 tips",
        description: "Een goed CV is cruciaal voor je sollicitatie! Dit is het eerste (en soms het enige) document waar een recruiter naar kijkt. Gebruik onderstaande tips voor het maken van jouw CV.",
        longDescription: "• Tip 1: Zet bovenaan je CV jouw persoonlijke profiel. Dit is een krachtige omschrijving van jezelf van max. 6 zinnen.\n\n• Tip 2: Beschrijf alle ervaringen in omgekeerde chronologische volgorde met concrete, kwantificeerbare resultaten. Gebruik hierbij actieve werkwoorden om jouw ervaringen te beschrijven.\n\n• Tip 3: Focus op je skills! Zorg ervoor dat je je vaardigheden duidelijk opsomt en je deze matched met de vacature waar je op solliciteert.\n\n• Tip 4: Heb je een 'gat' in je CV omdat je bijvoorbeeld op reis bent geweest? Of ben je van studie geswitched? Benoem dit! Zorg ervoor\n\n• Tip 5: Houd je CV beknopt: het document mag maximaal 2 pagina's zijn.\n\n• Extra tip: Laat iemand anders je CV nalezen op spelfouten en leesbaarheid.",
        icon: <AssignmentIcon />,
        isPremium: false
      },
      {
        title: "Authentiek blijven tijdens interviews",
        description: "Bereid je goed voor op veelgestelde vragen, maar blijf vooral jezelf. Eerlijkheid en authenticiteit komen altijd beter over dan ingestudeerde antwoorden.",
        longDescription: "Authenticiteit is wat je onderscheidt van andere kandidaten. Bereid je voor door minstens 5 concrete voorbeelden te bedenken van situaties waarin je uitdagingen hebt overwonnen. Oefen het STAR-model (Situatie, Taak, Actie, Resultaat) voor het structureren van je antwoorden. Onderzoek het bedrijf en stel zelf ook kritische vragen die aantonen dat je hebt nagedacht over de rol. Wees eerlijk over je zwakke punten, maar toon aan hoe je eraan werkt. En tot slot: lichaamstaal is essentieel – maak oogcontact, let op je houding en oefen eventueel met een video-opname.",
        icon: <PersonIcon />,
        isPremium: false
      },
      {
        title: "LinkedIn netwerk uitbreiden",
        description: "Stuur persoonlijke connectieverzoeken en reageer actief op content uit je netwerk. Zorg voor een professionele profielfoto en een uitgebreide samenvatting.",
        longDescription: "Tip 1: Een sterk LinkedIn-netwerk begint met een volledig profiel. Zorg hierbij voor een professionele foto waar je gezicht goed zichtbaar is, en een banner die bij je professionele identiteit past.\n\nTip 2: Bij connectieverzoeken: stuur ALTIJD een persoonlijk bericht mee. Als je de persoon niet kent, stuur dan waarom je graag connecties wilt worden.\n\nTip 3: Wees actief door wekelijks te reageren op berichten in je timeline en deel zelf ook inhoudelijke content. Het is handig om ook lid te worden van relevante groepen in jouw vakgebied, zodat je kan participeren in deze discussies.",
        icon: <WorkIcon />,
        isPremium: false
      },
      {
        title: "Werk-privé balans bewaken",
        description: "Stel duidelijke grenzen, plan ontspanning in je agenda zoals je werkafspraken plant, en durf 'nee' te zeggen tegen extra taken als je capaciteit bereikt is.",
        longDescription: "Een gezonde werk-privé balans is essentieel voor duurzame prestaties. Begin met het vastleggen van je werkuren en houd je daaraan. Maak aparte e-mailadressen voor werk en privé, en schakel werknotificaties uit buiten werktijd. Plan bewust hersteltijd in je agenda, zoals je ook vergaderingen inplant. Leer nee zeggen door beleefd maar duidelijk jouw grenzen aan te geven. Neem volledige pauzes zonder je telefoon of computer. Reserveer tijd voor lichaamsbeweging, of het nu een wandeling, yoga of een intensieve workout is. Evalueer maandelijks je energie-niveau en pas je planning daarop aan.",
        icon: <SchoolIcon />,
        isPremium: false
      },
      {
        title: "Uitgebreide handleidingen en templates",
        description: "Toegang tot CV templates en stap-voor-stap gidsen om zelf je plan op te stellen, met aandachtspunten voor korte- en langetermijndoelen.",
        longDescription: "Een effectief persoonlijk ontwikkelplan (POP) geeft richting aan je carrière. Begin met een grondige zelfanalyse: wat zijn je sterke punten, zwakke punten, waarden en passies? Definieer vervolgens zowel korte termijn doelen (6-12 maanden) als lange termijn ambities (3-5 jaar). Maak deze doelen SMART: Specifiek, Meetbaar, Acceptabel, Realistisch en Tijdgebonden. Identificeer welke vaardigheden, kennis en ervaringen je nodig hebt om deze doelen te bereiken. Schets concrete acties per doel en bepaal hoe en wanneer je voortgang gaat meten. Deel je POP met een mentor of coach die je kan helpen bij het reflecteren en bijsturen wanneer nodig.",
        icon: <PersonIcon />,
        isPremium: true
      },
      {
        title: "Salarisonderhandeling voorbereiden",
        description: "Exclusieve strategieën en scripts om het maximum uit je salarisonderhandeling te halen, inclusief timing, benchmarks en tegenargumenten.",
        longDescription: "Succesvolle salarisonderhandeling begint met gedegen marktonderzoek. Raadpleeg brancherapporten, salarissites en je netwerk om het marktconforme salaris voor jouw functie te bepalen. Documenteer je waarde met concrete voorbeelden van successen en resultaten die je hebt behaald. Bepaal je onderhandelingsmarge: je minimale acceptabele salaris, je streefbedrag en je openingsbod (10-20% boven marktwaarde). Bereid antwoorden voor op veelvoorkomende tegenwerpingen. Timing is cruciaal: wacht tot er een formeel aanbod is voordat je over salaris begint. Vergeet niet het totale pakket te overwegen, inclusief secundaire arbeidsvoorwaarden zoals opleidingsbudget, flexibiliteit en bonusregelingen.",
        icon: <PaymentsIcon />,
        isPremium: true
      },
      {
        title: "Ontdek je unieke kwaliteiten",
        description: "Wetenschappelijk onderbouwde oefeningen om jouw unieke vaardigheden en kernkwaliteiten te bepalen.",
        longDescription: "Als introvert kun je effectief netwerken op een manier die bij jou past. Bereid je goed voor door vooraf 3-5 gespreksonderwerpen en vragen te bedenken. Kom vroeg op netwerkevenementen wanneer de groepen nog klein zijn. Stel kwaliteit boven kwantiteit: focus op 2-3 betekenisvolle gesprekken in plaats van zoveel mogelijk contacten. Luisteren is jouw kracht als introvert; gebruik het om oprechte, diepgaande vragen te stellen. Plan 'herstel-tijd' na netwerkactiviteiten. Overweeg een extravert als 'netwerkbuddy' die je kan introduceren. Online netwerken via LinkedIn of vakspecifieke forums kan ook effectief zijn. Tot slot: geef jezelf toestemming om events te verlaten wanneer je energie op is - kwaliteit gaat voor kwantiteit.",
        icon: <PsychologyIcon />,
        isPremium: true
      }
    ],
    en: [
      {
        title: "Enhance your resume with these 5 tips",
        description: "A good resume is crucial for your job application! This is the first (and sometimes the only) document a recruiter looks at. Use the tips below to create your resume.",
        longDescription: "• Tip 1: Put your personal profile at the top of your resume. This is a powerful description of yourself with a maximum of 6 sentences.\n\n• Tip 2: Describe all experiences in reverse chronological order with concrete, quantifiable results. Use active verbs to describe your experiences.\n\n• Tip 3: Focus on your skills! Make sure you clearly list your skills and match them with the job you're applying for.\n\n• Tip 4: Do you have a 'gap' in your resume because you've been traveling, for example? Or have you switched studies? Mention this! Make sure to explain it.\n\n• Tip 5: Keep your resume concise: the document should be a maximum of 2 pages.\n\n• Extra tip: Have someone else proofread your resume for spelling errors and readability.",
        icon: <AssignmentIcon />,
        isPremium: false
      },
      {
        title: "Staying authentic during interviews",
        description: "Prepare well for common questions, but most importantly, be yourself. Honesty and authenticity always come across better than rehearsed answers.",
        longDescription: "Authenticity is what sets you apart from other candidates. Prepare by thinking of at least 5 concrete examples of situations where you overcame challenges. Practice the STAR model (Situation, Task, Action, Result) for structuring your answers. Research the company and ask critical questions yourself that show you've thought about the role. Be honest about your weaknesses, but show how you're working on them. And finally: body language is essential - make eye contact, pay attention to your posture, and possibly practice with a video recording.",
        icon: <PersonIcon />,
        isPremium: false
      },
      {
        title: "Expanding your LinkedIn network",
        description: "Send personalized connection requests and actively respond to content from your network. Ensure you have a professional profile picture and a comprehensive summary.",
        longDescription: "Tip 1: A strong LinkedIn network starts with a complete profile. Make sure you have a professional photo where your face is clearly visible, and a banner that matches your professional identity.\n\nTip 2: With connection requests: ALWAYS include a personal message. If you don't know the person, explain why you'd like to connect.\n\nTip 3: Be active by responding weekly to messages in your timeline and sharing substantive content yourself. It's also helpful to join relevant groups in your field so you can participate in these discussions.",
        icon: <WorkIcon />,
        isPremium: false
      },
      {
        title: "Maintaining work-life balance",
        description: "Set clear boundaries, schedule relaxation in your calendar just as you would work appointments, and dare to say 'no' to extra tasks when you've reached your capacity.",
        longDescription: "A healthy work-life balance is essential for sustainable performance. Start by setting your working hours and sticking to them. Create separate email addresses for work and personal use, and turn off work notifications outside working hours. Consciously plan recovery time in your calendar, just as you would schedule meetings. Learn to say no by politely but clearly stating your boundaries. Take complete breaks without your phone or computer. Reserve time for physical exercise, whether it's walking, yoga, or an intensive workout. Evaluate your energy level monthly and adjust your planning accordingly.",
        icon: <SchoolIcon />,
        isPremium: false
      },
      {
        title: "Comprehensive guides and templates",
        description: "Access to resume templates and step-by-step guides to create your own plan, with focus points for short and long-term goals.",
        longDescription: "An effective personal development plan (PDP) gives direction to your career. Start with a thorough self-analysis: what are your strengths, weaknesses, values, and passions? Then define both short-term goals (6-12 months) and long-term ambitions (3-5 years). Make these goals SMART: Specific, Measurable, Achievable, Realistic, and Time-bound. Identify which skills, knowledge, and experiences you need to achieve these goals. Outline concrete actions for each goal and determine how and when you will measure progress. Share your PDP with a mentor or coach who can help you reflect and adjust when necessary.",
        icon: <PersonIcon />,
        isPremium: true
      },
      {
        title: "Preparing for salary negotiations",
        description: "Exclusive strategies and scripts to maximize your salary negotiation, including timing, benchmarks, and counter-arguments.",
        longDescription: "Successful salary negotiation begins with thorough market research. Consult industry reports, salary websites, and your network to determine the market-competitive salary for your position. Document your value with concrete examples of successes and results you've achieved. Determine your negotiation margin: your minimum acceptable salary, your target amount, and your opening bid (10-20% above market value). Prepare answers to common objections. Timing is crucial: wait until there is a formal offer before discussing salary. Don't forget to consider the total package, including secondary benefits such as training budget, flexibility, and bonus schemes.",
        icon: <StarIcon />,
        isPremium: true
      },
      {
        title: "Discover your unique qualities",
        description: "Scientifically based exercises to determine your unique skills and core qualities.",
        longDescription: "As an introvert, you can network effectively in a way that suits you. Prepare well by thinking of 3-5 conversation topics and questions in advance. Arrive early at networking events when the groups are still small. Choose quality over quantity: focus on 2-3 meaningful conversations instead of as many contacts as possible. Listening is your strength as an introvert; use it to ask sincere, in-depth questions. Plan 'recovery time' after networking activities. Consider an extrovert as a 'networking buddy' who can introduce you. Online networking via LinkedIn or industry-specific forums can also be effective. Finally, give yourself permission to leave events when your energy is depleted - quality over quantity.",
        icon: <WorkIcon />,
        isPremium: true
      }
    ]
  };

  // Get the correct tips based on the current language
  const careerTips = careerTipsContent[language];

  // Toggle function for expanding/collapsing a tip
  const handleExpandClick = (index: number) => {
    setExpandedTips(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <>
      <PageHeader 
        title={language === 'nl' ? 'Carrièretips van een loopbaancoach' : translations[language].tips.title}
        subtitle={translations[language].tips.subtitle} 
      />

      {/* Long-form article index. Existing short tips remain available below. */}
      <Container component="main" maxWidth="lg" sx={{ pt: 2, pb: 7 }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography component="h2" variant="h2" sx={{ mb: 1.5 }}>
            Verdiepende loopbaanartikelen
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 720, mx: 'auto' }}>
            Heldere antwoorden en praktische stappen voor keuzes in je loopbaan.
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {articles.map((article) => (
            <Grid item xs={12} md={6} key={article.slug}>
              <Card
                component={RouterLink}
                to={`/tips/${article.slug}`}
                elevation={0}
                sx={{
                  display: 'block',
                  height: '100%',
                  color: 'inherit',
                  textDecoration: 'none',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography
                    component="time"
                    dateTime={article.publishedAt}
                    variant="body2"
                    color="text.secondary"
                  >
                    {new Intl.DateTimeFormat(language === 'nl' ? 'nl-NL' : 'en-CA', {
                      dateStyle: 'long'
                    }).format(new Date(`${article.publishedAt}T12:00:00`))}
                  </Typography>
                  <Typography component="h3" variant="h3" sx={{ mt: 1.5, mb: 2 }}>
                    {article.title}
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    {article.excerpt}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'secondary.dark', fontWeight: 600 }}>
                    Lees het artikel <ArrowForwardIcon sx={{ ml: 1, fontSize: 20 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Free Tips Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          {/* Typography with subtitle removed */}
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {careerTips.filter(tip => !tip.isPremium).map((tip, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                elevation={0} 
                sx={{ 
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box 
                      sx={{ 
                        borderRadius: '50%', 
                        width: 50, 
                        height: 50, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                        bgcolor: alpha('#8B7D6B', 0.1),
                        mr: 2
                      }}
                    >
                      {tip.icon}
                    </Box>
                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 500, flex: 1 }}>
                      {tip.title}
                    </Typography>
                    <ExpandMore
                      expand={!!expandedTips[index]}
                      onClick={() => handleExpandClick(index)}
                      aria-expanded={!!expandedTips[index]}
                      aria-label={translations[language].tips.aria.showMore}
                    >
                      <ExpandMoreIcon />
                    </ExpandMore>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {tip.description}
                  </Typography>
                  <Collapse in={!!expandedTips[index]} timeout="auto" unmountOnExit>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      paragraph 
                      sx={{ 
                        mt: 2,
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {tip.longDescription}
                    </Typography>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Premium Tips Preview Section - Updated with white boxes */}
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <PremiumIcon sx={{ color: 'secondary.main', mr: 1 }} />
            <Typography 
              variant="h6" 
              component="p" 
              sx={{ 
                color: 'secondary.main', 
                fontWeight: 500, 
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
              }}
            >
              {translations[language].tips.exclusiveContent}
            </Typography>
          </Box>
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 3, 
              fontWeight: 600, 
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: 'primary.main' 
            }}
          >
            {translations[language].tips.premiumTips}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: 700, 
              mx: 'auto', 
              color: 'text.secondary',
              mb: 5
            }}
          >
            {translations[language].tips.premiumDescription}
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {careerTips.filter(tip => tip.isPremium).map((tip, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                elevation={2} 
                sx={{ 
                  height: '100%',
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }
                }}
              >
                <Chip 
                  label="Premium" 
                  color="secondary" 
                  size="small"
                  icon={<StarIcon />}
                  sx={{ 
                    position: 'absolute', 
                    top: -10, 
                    right: 20,
                    fontWeight: 600,
                    borderRadius: '12px',
                    px: 1
                  }}
                />
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box 
                      sx={{ 
                        borderRadius: '50%', 
                        width: 50, 
                        height: 50, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        bgcolor: 'secondary.main',
                        mr: 2,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      {tip.icon}
                    </Box>
                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 600, flex: 1 }}>
                      {tip.title}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {tip.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* Single Button for Premium Tips */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <MuiLink
            component={RouterLink}
            to="/diensten/zelfcoaching?scrollTo=pakketten"
            sx={{ textDecoration: 'none' }}
          >
            <Button
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                borderRadius: '6px',
                py: 1.5,
                px: 3,
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
              }}
            >
              {language === 'nl' ? 'Bekijk Premium Pakketten' : 'View Premium Packages'}
            </Button>
          </MuiLink>
        </Box>
      </Container>

      {/* Premium call-to-action section */}
      <Box sx={{ 
        my: 8, 
        p: { xs: 3, sm: 4, md: 5 }, 
        borderRadius: 2, 
        bgcolor: 'background.paper',
        boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        textAlign: 'center',
        maxWidth: 1000,
        mx: 'auto'
      }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {translations[language].tips.premiumTitle}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: 800, mx: 'auto' }}>
            {translations[language].tips.premiumDescription}
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircleIcon color="secondary" sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {translations[language].tips.premiumBenefit1}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircleIcon color="secondary" sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {translations[language].tips.premiumBenefit2}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircleIcon color="secondary" sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {translations[language].tips.premiumBenefit3}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <MuiLink
            component={RouterLink}
            to="/diensten/zelfcoaching?scrollTo=pakketten"
            sx={{ textDecoration: 'none' }}
          >
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              startIcon={<ArrowForwardIcon />}
              sx={{ 
                borderRadius: '6px', 
                py: 1.5, 
                px: 3, 
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
              }}
            >
              {translations[language].tips.discoverPackages}
            </Button>
          </MuiLink>
        </Box>
      </Box>
    </>
  );
};

export default Tips;
